import {
  IStepValueSearchPreferences,
  TWizardStepComponentEditable,
  TStepPayload,
  TStepErrors,
  WIZARD_STUDENT_STEPS_PATHS,
  TWizardStepComponentEditableView,
  IWizardStudentState
} from "../../../interfaces";
import { Button, DatePicker, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import {
  formatAsUTCToServerDate,
  formatDateToShort,
  parseServerDate
} from "utils/time";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { UserAPI } from "../../../../../API/userApi";
import { getChangedFields, getStepErrorsFromResponse } from "../../../utils";
import PlacesAutocomplete from "../../../../Map/PlacesAutocomplete";
import { MapInitializer } from "../../../../Map/MapInitializer";
import {
  StudentAmountLookingPeopleValues,
  TStudentAmountLookingPeople
} from "../../../../../interfaces/interfaces";
import styled, { css } from "styled-components/macro";
import { useContextHospi } from "../../../../../context/ContextHospi";
import { useTranslation } from "react-i18next";
import { StepViewOnlyValue } from "../../styles";
import { DATE_PICKER_DATE_FORMAT } from "../../../../../constants";

const statePath = WIZARD_STUDENT_STEPS_PATHS.SearchPreferences;

export const WizardStepSearchPreferences: TWizardStepComponentEditable<IWizardStudentState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<
      TStepPayload<IStepValueSearchPreferences>
    >(state[statePath] ?? {});
    const [errors, setErrors] =
      useState<TStepErrors<IStepValueSearchPreferences>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);

        return UserAPI.wizardStudentSaveSearchPreferences(values)
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);

        return UserAPI.wizardStudentSaveSearchPreferencesPartially(
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepSearchPreferencesView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const PlacesFormItem = styled(Form.Item)`
  grid-column-start: span 2;
`;
const PeopleAmountItem = styled(Form.Item)`
  grid-column-start: span 2;
`;

const Root = styled(Form)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;

    ${PlacesFormItem} {
      grid-column-start: auto;
    }
    ${PeopleAmountItem} {
      grid-column-start: auto;
    }
  }
`;
const SelectForPeopleAmount = styled(Select)`
  .ant-select-selector {
    height: 60px !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    display: flex;
    align-items: center;
  }
`;
const PeopleAmountInputContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 16px;
  row-gap: 16px;

  @media ${(props) => props.theme.media.phone} {
    grid-template-columns: 1fr;
  }
`;
const ButtonForPeopleAmount = styled(Button)<{ $selected: boolean }>`
  padding: 16px;
  height: auto;
  text-align: start;
  font-size: 16px;

  ${(p) =>
    p.$selected
      ? css`
          border: 1px solid ${(p) => p.theme.colors.primary};
          background: #fff7f3;
        `
      : css``}
`;
export const WizardStepSearchPreferencesView: TWizardStepComponentEditableView<IStepValueSearchPreferences> =
  React.memo(({ values, setValues, errors, readonly, viewonly }) => {
    const { currentUserRole } = useContextHospi();
    const { t } = useTranslation(currentUserRole);
    const inputSize = "large";

    const lookingPeopleOptions = StudentAmountLookingPeopleValues.map((v) => ({
      value: v,
      label: (
        t(
          `${currentUserRole}Profile.searchCriteria.form.amountLookingPeople.options`,
          {
            returnObjects: true
          }
        ) as { [key: string]: string }
      )[v]
    }));

    return (
      <Root layout="vertical">
        <MapInitializer>
          <PlacesFormItem
            label={t(
              `${currentUserRole}Profile.searchCriteria.form.searchLocationString.title`
            )}
            validateStatus={errors?.search_location_string ? "error" : ""}
            help={errors?.search_location_string}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {values.search_location_string ?? ""}
              </StepViewOnlyValue>
            ) : (
              <PlacesAutocomplete
                size={inputSize}
                disabled={readonly}
                displayValue={
                  values.search_location_string
                    ? {
                        displayName: values.search_location_string,
                        location: {
                          lat: 0,
                          lng: 0
                        }
                      }
                    : undefined
                }
                placeholder={t(
                  `${currentUserRole}Profile.searchCriteria.form.searchLocationString.placeholder`
                )}
                onChange={(value, data) =>
                  setValues({
                    ...values,
                    search_location_string: data?.description
                  })
                }
              />
            )}
          </PlacesFormItem>
          <Form.Item
            label={t(
              `${currentUserRole}Profile.searchCriteria.form.moveInDate.title`
            )}
            validateStatus={errors?.preferred_move_in ? "error" : ""}
            help={errors?.preferred_move_in}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {values.preferred_move_in
                  ? formatDateToShort(parseServerDate(values.preferred_move_in))
                  : ""}
              </StepViewOnlyValue>
            ) : (
              <DatePicker
                size={inputSize}
                disabled={readonly}
                value={
                  values.preferred_move_in
                    ? dayjs(parseServerDate(values.preferred_move_in))
                    : undefined
                }
                style={{
                  width: "100%"
                }}
                format={DATE_PICKER_DATE_FORMAT}
                placeholder={t(
                  `${currentUserRole}Profile.searchCriteria.form.moveInDate.placeholder`
                )}
                onChange={(date) => {
                  const resetMoveOut =
                    values.preferred_move_out && date
                      ? date.startOf("day").toDate().getTime() >
                        parseServerDate(values.preferred_move_out).getTime()
                      : false;

                  setValues({
                    ...values,
                    preferred_move_in: date
                      ? formatAsUTCToServerDate(date.toDate())
                      : undefined,
                    preferred_move_out: resetMoveOut
                      ? null
                      : values.preferred_move_out
                  });
                }}
                disabledDate={(date) => {
                  return (
                    date.toDate().getTime() <=
                    dayjs(new Date()).startOf("day").toDate().getTime()
                  );
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(
              `${currentUserRole}Profile.searchCriteria.form.moveOutDate.title`
            )}
            validateStatus={errors?.preferred_move_out ? "error" : ""}
            help={errors?.preferred_move_out}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {values.preferred_move_out
                  ? formatDateToShort(
                      parseServerDate(values.preferred_move_out)
                    )
                  : ""}
              </StepViewOnlyValue>
            ) : (
              <DatePicker
                size={inputSize}
                disabled={readonly}
                format={DATE_PICKER_DATE_FORMAT}
                value={
                  values.preferred_move_out
                    ? dayjs(parseServerDate(values.preferred_move_out))
                    : undefined
                }
                style={{
                  width: "100%"
                }}
                placeholder={t(
                  `${currentUserRole}Profile.searchCriteria.form.moveOutDate.placeholder`
                )}
                onChange={(date) =>
                  setValues({
                    ...values,
                    preferred_move_out: date
                      ? formatAsUTCToServerDate(date.toDate())
                      : undefined
                  })
                }
                disabledDate={(date) => {
                  if (
                    (values.preferred_move_in &&
                      date.toDate().getTime() <
                        parseServerDate(values.preferred_move_in).getTime()) ||
                    date.toDate().getTime() <=
                      dayjs(new Date()).startOf("day").toDate().getTime()
                  ) {
                    return true;
                  }

                  return false;
                }}
              />
            )}
          </Form.Item>
          <PeopleAmountItem
            label={t(
              `${currentUserRole}Profile.searchCriteria.form.amountLookingPeople.title`
            )}
            validateStatus={errors?.amount_looking_people ? "error" : ""}
            help={errors?.amount_looking_people}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {lookingPeopleOptions.find(
                  (o) => o.value === values.amount_looking_people
                )?.label ?? ""}
              </StepViewOnlyValue>
            ) : (
              <PeopleAmountInputContainer>
                <ButtonForPeopleAmount
                  onClick={() => {
                    setValues({ ...values, amount_looking_people: "myself" });
                  }}
                  $selected={values.amount_looking_people === "myself"}
                  danger={!!errors?.amount_looking_people}
                  disabled={readonly}
                >
                  {t(
                    `${currentUserRole}Profile.searchCriteria.form.amountLookingPeople.options.myself`
                  )}
                </ButtonForPeopleAmount>
                <SelectForPeopleAmount
                  size={inputSize}
                  disabled={readonly}
                  value={
                    values.amount_looking_people === "myself"
                      ? null
                      : values.amount_looking_people
                  }
                  placeholder={t(
                    `${currentUserRole}Profile.searchCriteria.form.amountLookingPeople.placeholder`
                  )}
                  options={lookingPeopleOptions.filter(
                    (o) => o.value !== "myself"
                  )}
                  onChange={(newValue) =>
                    setValues({
                      ...values,
                      amount_looking_people:
                        newValue as TStudentAmountLookingPeople
                    })
                  }
                />
              </PeopleAmountInputContainer>
            )}
          </PeopleAmountItem>
          <Form.Item
            label={t(
              `${currentUserRole}Profile.searchCriteria.form.budget.title`
            )}
            validateStatus={errors?.max_budget ? "error" : ""}
            help={errors?.max_budget}
          >
            {viewonly ? (
              <StepViewOnlyValue>{values.max_budget ?? ""}</StepViewOnlyValue>
            ) : (
              <Input
                size={inputSize}
                type="number"
                prefix="€"
                disabled={readonly}
                value={values.max_budget ?? undefined}
                placeholder={t(
                  `${currentUserRole}Profile.searchCriteria.form.budget.placeholder`
                )}
                onChange={(e) =>
                  setValues({
                    ...values,
                    max_budget: parseInt(e.target.value, 10)
                  })
                }
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(
              `${currentUserRole}Profile.searchCriteria.form.area.title`
            )}
            validateStatus={errors?.min_area ? "error" : ""}
            help={errors?.min_area}
          >
            {viewonly ? (
              <StepViewOnlyValue>{values.min_area ?? ""}</StepViewOnlyValue>
            ) : (
              <Input
                size={inputSize}
                type="number"
                suffix="m2"
                disabled={readonly}
                value={values.min_area ?? undefined}
                placeholder={t(
                  `${currentUserRole}Profile.searchCriteria.form.area.placeholder`
                )}
                onChange={(e) =>
                  setValues({
                    ...values,
                    min_area: parseInt(e.target.value, 10)
                  })
                }
              />
            )}
          </Form.Item>
        </MapInitializer>
      </Root>
    );
  });
